import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Search, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import trLogoWhite from "@/assets/lp-logo-white.png";
import { RailTooltip } from "./NavItem";
import { NEW_QUERY_PATH } from "./nav-config";
import type { SidebarClasses } from "./sidebar-theme";
import { isMacPlatform } from "./useNewQueryShortcut";

const focusRing = (classes: SidebarClasses) => cn("outline-none focus-visible:ring-2", classes.ring);

interface BrandProps {
  rail: boolean;
  partnerLogo: string | null;
  partnerName: string;
  homeLabel: string;
  classes: SidebarClasses;
  onNavigate?: () => void;
}

/** Logo (figma-spec §4.1 / §6): asset local TradeRisk, favicon no rail, ou `logoUrl` do parceiro só em `<img src>` (C5). */
export function SidebarBrand({ rail, partnerLogo, partnerName, homeLabel, classes, onNavigate }: BrandProps) {
  let content: ReactNode;
  if (partnerLogo) {
    const size = rail ? "h-10 w-10 rounded-[10px]" : "h-8 max-w-[180px]";
    content = <img src={partnerLogo} alt={partnerName} className={cn("object-contain", size)} />;
  } else if (rail) {
    content = <img src="/favicon.png?v=3" alt="TradeRisk 360" className="h-10 w-10 rounded-[10px] object-contain" />;
  } else {
    content = <img src={trLogoWhite} alt="TradeRisk 360" className="h-[68px] w-[137px] object-contain pl-1" />;
  }
  return (
    <Link
      to="/app"
      aria-label={homeLabel}
      onClick={onNavigate}
      className={cn(rail ? "rounded-[10px]" : "rounded-[6px]", focusRing(classes))}
    >
      {content}
    </Link>
  );
}

interface IconButtonProps {
  label: string;
  icon: LucideIcon;
  rail: boolean;
  classes: SidebarClasses;
  onClick: () => void;
}

/** Botão recolher/expandir/fechar: 28×28 no cabeçalho, 48×40 no rail (com tooltip). */
export function SidebarIconButton({ label, icon: Icon, rail, classes, onClick }: IconButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[8px] border",
        rail ? "mt-2 h-10 w-12" : "h-7 w-7",
        classes.border,
        classes.hover,
        focusRing(classes),
      )}
    >
      <Icon className={cn(rail ? "h-5 w-5" : "h-4 w-4", classes.icon)} aria-hidden="true" />
    </button>
  );
  return rail ? <RailTooltip label={label}>{button}</RailTooltip> : button;
}

interface NewQueryProps {
  rail: boolean;
  label: string;
  classes: SidebarClasses;
  onNavigate?: () => void;
}

/** Nova consulta (S4): só navega para `/app/companies/new`; o atalho é registrado por `useNewQueryShortcut`. */
export function NewQueryButton({ rail, label, classes, onNavigate }: NewQueryProps) {
  const mac = isMacPlatform();
  const common = { to: NEW_QUERY_PATH, onClick: onNavigate, "aria-keyshortcuts": mac ? "Meta+K" : "Control+K" };

  if (rail) {
    return (
      <RailTooltip label={label}>
        <Link
          {...common}
          aria-label={label}
          className={cn(
            "flex h-10 w-12 items-center justify-center rounded-[8px]",
            classes.primary,
            focusRing(classes),
          )}
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </Link>
      </RailTooltip>
    );
  }

  return (
    <Link
      {...common}
      className={cn(
        "mt-2.5 flex h-[38px] shrink-0 items-center justify-center gap-2 rounded-[8px] px-3",
        "text-[13.5px] font-bold leading-[18px]",
        classes.primary,
        focusRing(classes),
      )}
    >
      <Search className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      {label}
      <kbd
        className={cn("rounded-[4px] px-[5px] py-px font-dm text-[10.5px] font-semibold leading-[14px]", classes.kbd)}
      >
        {mac ? "⌘K" : "Ctrl K"}
      </kbd>
    </Link>
  );
}
